import React from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { parse, stringify } from '../../../util/urlHelpers';
import { parseSelectFilterOptions, constructQueryParamName } from '../../../util/search';
import { formatCurrencyMajorUnit } from '../../../util/currency';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_LONG } from '../../../util/types';
import {
  TAG_PARAM,
  tagChipName,
  tagSlugsFromParam,
  searchWithoutTagSlug,
} from '../../../util/listingTagSearch';

import css from './AppliedFilterChips.module.css';

const RemoveIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </svg>
);

// Format a selected-options array back to the URL param value the filters use,
// mirroring SelectMultipleFilter's format helper (incl. searchMode prefix).
const formatSelectedOptions = (selectedOptions, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  return hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
};

/**
 * AppliedFilterChips — one removable chip per active search refinement:
 * a gold keyword chip (AI sparkle) for keywords/aiKeywords, one chip per
 * selected enum filter value, and a formatted price-range chip. Plus a
 * "Clear all" link. Renders nothing when there is nothing active.
 *
 * Filter-value removals go through getHandleChangedValueFn so the existing
 * chip_modified telemetry fires (Story 8.2 / AC9). The listing-tag chip is the
 * exception: it fires onChipModified itself and rewrites only its own URL
 * segment (Story 8.18, review M2).
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Object} props.location - React Router location
 * @param {Object} props.validQueryParams - Valid filter query params from getDerivedRenderData
 * @param {Array<Object>} props.availableFilters - Filter configs from getDerivedRenderData
 * @param {Function} props.getHandleChangedValueFn - Returns the filter change handler (fires chip_modified)
 * @param {Function} props.onClearAll - Clears all filters AND keywords/aiKeywords
 * @param {Function} [props.onChipModified] - Fires chip_modified for a removal that bypasses getHandleChangedValueFn
 * @param {string} props.marketplaceCurrency - e.g. 'USD'
 * @returns {JSX.Element|null}
 */
const AppliedFilterChips = props => {
  const intl = useIntl();
  const history = useHistory();
  const {
    rootClassName,
    className,
    location,
    validQueryParams,
    availableFilters,
    getHandleChangedValueFn,
    onClearAll,
    onChipModified,
    marketplaceCurrency,
  } = props;

  const searchParams = parse(location.search);
  // Same precedence as maybeRecordSearch (SearchPageWithGrid): aiKeywords is the
  // original AI-parsed query, keywords the plain one.
  const keywordValue = searchParams.aiKeywords ?? searchParams.keywords;

  const removeKeyword = () => {
    const { keywords, aiKeywords, ...rest } = parse(location.search);
    const searchString = stringify(rest);
    history.push(`${location.pathname}${searchString ? `?${searchString}` : ''}`);
  };

  const chips = [];
  availableFilters.forEach(filterConfig => {
    const { key, scope, schemaType } = filterConfig;

    if (schemaType === 'price') {
      const priceValue = validQueryParams[key];
      if (!priceValue) {
        return;
      }
      const [minRaw, maxRaw] = `${priceValue}`.split(',');
      const min = Number.parseInt(minRaw, 10);
      const max = Number.parseInt(maxRaw, 10);
      if (!Number.isInteger(min) || !Number.isInteger(max)) {
        return;
      }
      const { min: configMin = 0, max: configMax } = filterConfig;
      const fmt = value => formatCurrencyMajorUnit(intl, marketplaceCurrency, value);
      const label =
        min <= configMin
          ? intl.formatMessage({ id: 'AppliedFilterChips.priceUnder' }, { price: fmt(max) })
          : max >= configMax
          ? intl.formatMessage({ id: 'AppliedFilterChips.priceOver' }, { price: fmt(min) })
          : intl.formatMessage(
              { id: 'AppliedFilterChips.priceRange' },
              { minPrice: fmt(min), maxPrice: fmt(max) }
            );
      chips.push({
        key: `price.${priceValue}`,
        label,
        onRemove: () => getHandleChangedValueFn(true)({ [key]: null }),
      });
      return;
    }

    // Long (integer range) filters, e.g. Birth Year: the whole range is ONE
    // removable chip — "2018 – 2022", "From 2018" or "Up to 2022". Open-ended
    // param shapes ("2018," / ",2022") are valid.
    if (schemaType === SCHEMA_TYPE_LONG) {
      const paramName = constructQueryParamName(key, scope);
      const paramValue = validQueryParams[paramName];
      if (paramValue == null || paramValue === '') {
        return;
      }
      const [minRaw, maxRaw] = `${paramValue}`.split(',');
      const minValue = Number.parseInt(minRaw, 10);
      const maxValue = Number.parseInt(maxRaw, 10);
      const hasMin = Number.isInteger(minValue);
      const hasMax = Number.isInteger(maxValue);
      if (!hasMin && !hasMax) {
        return;
      }
      // String values: years must render as "2018", never locale-grouped "2,018"
      const rangeValues = { minValue: `${minValue}`, maxValue: `${maxValue}` };
      const label =
        hasMin && hasMax
          ? intl.formatMessage({ id: 'AppliedFilterChips.rangeBetween' }, rangeValues)
          : hasMin
          ? intl.formatMessage({ id: 'AppliedFilterChips.rangeFrom' }, rangeValues)
          : intl.formatMessage({ id: 'AppliedFilterChips.rangeTo' }, rangeValues);
      chips.push({
        key: `${paramName}.${paramValue}`,
        label,
        onRemove: () => getHandleChangedValueFn(true)({ [paramName]: null }),
      });
      return;
    }

    // Enum-like filters: custom listing fields + built-in listingType.
    const isEnumLike =
      [SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM].includes(schemaType) ||
      schemaType === 'listingType';
    if (!isEnumLike) {
      return;
    }
    const paramName = constructQueryParamName(key, scope);
    const paramValue = validQueryParams[paramName];
    if (paramValue == null || paramValue === '') {
      return;
    }
    const enumOptions = filterConfig.enumOptions || filterConfig.options || [];
    const searchMode = filterConfig.filterConfig?.searchMode;
    const selectedOptions = parseSelectFilterOptions(`${paramValue}`);

    selectedOptions.forEach(selectedValue => {
      const foundOption = enumOptions.find(o => `${o.option}` === `${selectedValue}`);
      chips.push({
        key: `${paramName}.${selectedValue}`,
        label: foundOption?.label || selectedValue,
        onRemove: () => {
          const remaining = selectedOptions.filter(v => v !== selectedValue);
          getHandleChangedValueFn(true)({
            [paramName]: formatSelectedOptions(remaining, schemaType, searchMode),
          });
        },
      });
    });
  });

  // Story 8.18 (ID-5) — the listing-tag filter is a CLI search key, so no
  // filter config describes it: the loop above never sees it, and
  // validQueryParams (pickSearchParamsOnly) drops it. Read it from the URL, as
  // the keyword chip does — loadData sends it to Sharetribe from there. One
  // "Described as: …" chip per slug; an unknown slug shows as itself, because
  // a filter the buyer cannot see or undo is never allowed.
  //
  // Removal (review M2) does NOT go through getHandleChangedValueFn: that
  // handler re-normalises every hosted multi-enum from the config's
  // searchMode, so removing a tag turned "barrel OR pole" into "barrel AND
  // pole". It rewrites the tag segment alone, every other param byte-identical,
  // and fires chip_modified through onChipModified as the handler would.
  const tagParamValue = searchParams[TAG_PARAM];
  if (tagParamValue != null && tagParamValue !== '') {
    const selectedTags = tagSlugsFromParam(`${tagParamValue}`);
    selectedTags.forEach(slug => {
      chips.push({
        key: `${TAG_PARAM}.${slug}`,
        label: intl.formatMessage(
          { id: 'AppliedFilterChips.describedAs' },
          { label: tagChipName(slug) }
        ),
        onRemove: () => {
          if (onChipModified) {
            onChipModified();
          }
          const nextSearch = searchWithoutTagSlug(location.search, slug);
          history.push(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`);
        },
      });
    });
  }

  const hasKeyword = keywordValue != null && `${keywordValue}`.length > 0;
  if (!hasKeyword && chips.length === 0) {
    return null;
  }

  const classes = classNames(rootClassName || css.root, className);
  const removeLabel = label =>
    intl.formatMessage({ id: 'AppliedFilterChips.removeFilter' }, { label });

  return (
    <div className={classes}>
      {hasKeyword ? (
        <span className={classNames(css.chip, css.keywordChip)}>
          <span className={css.sparkle} aria-hidden="true">
            ✦
          </span>
          “{keywordValue}”
          <button
            type="button"
            className={css.removeButton}
            onClick={removeKeyword}
            aria-label={removeLabel(`${keywordValue}`)}
          >
            <RemoveIcon />
          </button>
        </span>
      ) : null}
      {chips.map(chip => (
        <span key={chip.key} className={css.chip}>
          {chip.label}
          <button
            type="button"
            className={css.removeButton}
            onClick={chip.onRemove}
            aria-label={removeLabel(chip.label)}
          >
            <RemoveIcon />
          </button>
        </span>
      ))}
      <button type="button" className={css.clearAll} onClick={onClearAll}>
        <FormattedMessage id="AppliedFilterChips.clearAll" />
      </button>
    </div>
  );
};

export default AppliedFilterChips;
